import { useEffect, useState, useCallback } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { levelColor } from "../components/badges";

const LEVELS = ["Tous", "info", "warning", "error"];

export default function Logs() {
  const [tab, setTab] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [level, setLevel] = useState("Tous");

  const load = useCallback(() => {
    if (tab === "logs") {
      const q = level === "Tous" ? "" : `?level=${level}`;
      api.get(`/logs${q}`).then(({ data }) => setLogs(data)).catch(() => {});
    } else {
      api.get("/audit").then(({ data }) => setAudit(data)).catch(() => {});
    }
  }, [level, tab]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const verify = async () => {
    try {
      const { data } = await api.get("/audit/verify");
      data.valid
        ? toast.success(`Piste d'audit intègre — ${data.entries} entrées, chaîne de hash valide`)
        : toast.error(`INTÉGRITÉ COMPROMISE à la séquence ${data.broken_at_seq}`);
    } catch (e) {
      toast.error("Vérification impossible");
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleTimeString("fr-FR") + " " + new Date(iso).toLocaleDateString("fr-FR");

  return (
    <div data-testid="logs-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Journaux</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Journal d'activité et piste d'audit immuable</p>
        </div>
        {tab === "audit" && (
          <button data-testid="audit-verify-button" onClick={verify}
            className="flex items-center gap-2 border border-[#00E676]/40 bg-[#00E676]/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E676] transition-colors duration-150 hover:bg-[#00E676]/15">
            <ShieldCheck size={13} /> Vérifier l'intégrité
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <button data-testid="tab-logs" onClick={() => setTab("logs")}
          className={`border px-5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-150 ${tab === "logs" ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-neutral-500 hover:text-white"}`}>
          Journaux
        </button>
        <button data-testid="tab-audit" onClick={() => setTab("audit")}
          className={`border px-5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-150 ${tab === "audit" ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-neutral-500 hover:text-white"}`}>
          Piste d'audit
        </button>
        {tab === "logs" && (
          <div className="ml-4 flex gap-2">
            {LEVELS.map((l) => (
              <button key={l} data-testid={`log-filter-${l}`} onClick={() => setLevel(l)}
                className={`border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-150 ${level === l ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-neutral-500 hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "logs" ? (
        <div data-testid="logs-terminal" className="max-h-[70vh] overflow-y-auto border border-white/10 bg-black p-5 font-mono text-[11px] leading-relaxed shadow-[0_0_20px_rgba(0,229,255,0.05)]">
          {logs.length === 0 && <div className="text-neutral-600">— journal vide —</div>}
          {logs.map((l) => (
            <div key={l.id} className="whitespace-pre-wrap">
              <span className="text-neutral-600">[{fmt(l.created_at)}]</span>{" "}
              <span className={levelColor(l.level)}>[{l.level.toUpperCase()}]</span>{" "}
              {l.project_id && <span className="text-primary">[{l.project_id}]</span>}{" "}
              <span className="text-neutral-400">{l.event}</span>{" "}
              <span className={l.level === "error" ? "text-red-400" : l.level === "warning" ? "text-amber-300" : "text-neutral-300"}>— {l.detail}</span>
            </div>
          ))}
        </div>
      ) : (
        <div data-testid="audit-terminal" className="max-h-[70vh] overflow-y-auto border border-white/10 bg-black p-5 font-mono text-[11px] leading-relaxed shadow-[0_0_20px_rgba(0,229,255,0.05)]">
          {audit.length === 0 && <div className="text-neutral-600">— aucune entrée d'audit —</div>}
          {audit.map((a) => (
            <div key={a.id} className="whitespace-pre-wrap">
              <span className="text-neutral-600">#{a.seq} [{fmt(a.timestamp)}]</span>{" "}
              <span className={a.actor_type === "admin" ? "text-primary" : a.actor_type === "system" ? "text-amber-300" : "text-[#00E676]"}>[{a.actor}]</span>{" "}
              <span className="text-white">{a.action}</span>{" "}
              {a.project_id && <span className="text-neutral-400">projet={a.project_id}</span>}{" "}
              {a.correlation_id && <span className="text-violet-300">corr={a.correlation_id}</span>}{" "}
              <span className="text-neutral-600">hash={a.hash?.slice(0, 12)}…</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
